import { FormLabel } from '@chakra-ui/form-control';
import classNames from 'classnames';
import groupBy from 'lodash/groupBy';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createFilter, type GroupBase, type SingleValue } from 'react-select';
import { useQuery } from 'urql';

import { useGetLoggedInUser } from '@revelio/auth';
import { graphql } from '@revelio/data-access';
import { Button, Select } from '@revelio/ui';

import styles from './user-selector.module.css';

export interface UserOption {
  label: string;
  value: string;
  group: string;
  isGroupOption?: boolean;
  secondaryLabel?: string;
}

type GroupedUserOption = GroupBase<UserOption>;

const SEARCH_USER = graphql(`
  query DeliverablesUserSearch($input: String!) {
    userSearch(input: $input) {
      id
      name
      email
      client_name
      active
      live
    }
  }
`);

const HiddenGroupHeading = () => null;

const filterOption = createFilter<UserOption>({
  stringify: (option) =>
    `${option.data.group} ${option.data.label} ${option.data.secondaryLabel}`,
});

export function UserSelector({
  selectedUserOption,
  setSelectedUserOption,
  resetLabel = 'Reset to my deliverables',
  testId,
  selectableClientGroups = true,
}: {
  selectedUserOption?: UserOption;
  setSelectedUserOption: Dispatch<SetStateAction<UserOption | undefined>>;
  resetLabel?: string;
  testId?: string;
  selectableClientGroups?: boolean;
}) {
  const { loggedInUser } = useGetLoggedInUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);

  const isSelectedUserLoggedInUser =
    selectedUserOption?.value === loggedInUser?.id;

  const selectUser = (option: SingleValue<UserOption>) => {
    if (option) {
      setSelectedUserOption(option);
    }
  };

  const selectLoggedInUser = useCallback(() => {
    if (loggedInUser && loggedInUser.id) {
      const loggedInUserOption: UserOption | undefined = userOptions.find(
        (user) => user.value === loggedInUser.id
      );
      if (loggedInUserOption) setSelectedUserOption(loggedInUserOption);
    }
  }, [loggedInUser, setSelectedUserOption, userOptions]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    if (loggedInUser && loggedInUser?.id && loggedInUser?.name) {
      const loggedInUserOption: UserOption = {
        label: loggedInUser.name,
        value: loggedInUser.id,
        group: loggedInUser.client_name || '',
        secondaryLabel: loggedInUser.email || '',
      };

      setUserOptions((prevState) => {
        if (prevState.some((user) => user.value === loggedInUserOption.value)) {
          return prevState;
        } else {
          return [loggedInUserOption, ...prevState];
        }
      });
    }
  }, [loggedInUser]);

  const shouldSearch = debouncedTerm.length >= 3;

  const [{ data: userData, fetching: userLoading }] = useQuery({
    query: SEARCH_USER,
    variables: { input: debouncedTerm },
    pause: !shouldSearch,
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    const results = userData?.userSearch ?? [];
    if (!results) return;

    setUserOptions((prevState) => {
      const existing = new Map(prevState.map((u) => [u.value, u]));
      results.forEach((u) => {
        if (u && u.id && u.name && !existing.has(u.id)) {
          existing.set(u.id, {
            label: u.name ?? '',
            value: u.id ?? '',
            group: u.client_name ?? '',
            secondaryLabel: u.email ?? '',
          });
        }
      });
      return Array.from(existing.values());
    });
  }, [userData]);

  const groupedUserOptions: GroupedUserOption[] = useMemo(() => {
    const grouped = Object.entries(groupBy(userOptions, 'group')).map(
      ([label, options]): GroupedUserOption => ({ label, options })
    );

    if (!selectableClientGroups) return grouped;

    return grouped.map((group): GroupedUserOption => {
      if (!group.label) return group;
      const clientOption: UserOption = {
        isGroupOption: true,
        label: group.label,
        value: group.label,
        group: group.label,
      };
      return {
        ...group,
        options: [clientOption, ...(group.options ?? [])],
      };
    });
  }, [userOptions, selectableClientGroups]);

  return (
    <div className={styles.root} data-testid={testId}>
      <FormLabel fontSize="sm" className={styles.formLabel}>
        Search by user name, email, or client
      </FormLabel>
      <div className={styles.selectRow}>
        <div className={styles.wrapper}>
          <Select<UserOption, false, GroupedUserOption>
            size="sm"
            options={groupedUserOptions}
            isLoading={userLoading}
            isDisabled={false}
            placeholder="Type 3+ characters to search"
            onChange={selectUser}
            value={selectedUserOption}
            filterOption={filterOption}
            inputValue={searchTerm}
            onInputChange={(val) => setSearchTerm(val)}
            formatOptionLabel={(option) => (
              <div className={styles.userRow}>
                <div
                  className={
                    option.isGroupOption ? styles.groupLabel : styles.userLabel
                  }
                >
                  {option.label}
                </div>
                <div className={styles.userRowSpacer} />
                <div className={styles.secondaryLabel}>
                  {option.secondaryLabel}
                </div>
              </div>
            )}
            components={
              selectableClientGroups
                ? { GroupHeading: HiddenGroupHeading }
                : undefined
            }
          />
        </div>
        {!isSelectedUserLoggedInUser && (
          <Button
            size="sm"
            variant="link"
            className={classNames(styles.resetButton, styles.resetLink)}
            onClick={selectLoggedInUser}
          >
            {resetLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
